import React from "react"
import { isUndefined, isEmpty, get, first, set } from "lodash"
import styled, { css, keyframes } from "styled-components"
import { Button, notification } from "antd"
import Title from "../../components/Text/Title"
import Text from "../../components/Text/Text"
import { postData } from "../../api"
import withQueryString from "../../highOrderComponents/withQueryString"
import { media } from "../../components/mediaStylesNew"
import Form from "../Form"
import FormGroups from "./FormGroups"

const animateMenu = keyframes`
  0%{
    height: 100vh;
    //width: 100vw;
  }
  
  100% {
    height: 0px;
    left: -150%;
    ${media.desktop`
      left: -100%;
    `}
  }
`

const animateMenuIn = keyframes`
  0% {
    height: 0px;
    left: -150%;
    ${media.desktop`
    left: -100%;
    `}
  }
  100%{
    left: 0;
    height: 100%;
  }
`

const CustomLayout = styled.div`
  width: 100%;
  max-width: 100%;
  height: auto;
  padding-top: 15px;
  position: relative;
  z-index: 44;
  margin-top: 100px;
  margin-bottom: 100px;
  ${media.tablet`
    margin: 60px auto;
  `};
  ${media.largerPhone`
    max-width: 300px;
  `};
  ${props =>
    props.visible &&
    css`
      min-height: 300px;
    `}
`

const LoadingOverlay = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  text-align: center;
  padding: 15px;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background: white;
  transition: all 0.3s ease-out;
  min-height: 300px;
  pointer-events: none;
  z-index: 1000;
  opacity: ${props => (props.visible ? 1 : 0)};
  > * {
    line-height: 1.4em;
    margin-bottom: 15px;
  }
`

const Layout = styled.div`
  max-width: 800px;
  margin: auto;
  height: 100%;
  position: relative;
  display: flex;
  position: relative;
  z-index: 44;
  ${media.tablet`
    margin-top: 60px;
  `};
  ${media.largerPhone`
    max-width: 300px;
  `};
  flex-direction: column;
  align-items: center;
  left: 0;
  animation-fill-mode: both;
  ${props =>
    props.animate
      ? !props.show
        ? css`
            animation: ${animateMenu} 0.5s ease-out;
            animation-fill-mode: both;
          `
        : css`
            animation: ${animateMenuIn} 0.5s ease-out;
            animation-fill-mode: both;
          `
      : props.show
      ? css`
          height: 100%;
        `
      : css`
          height: 0px;
          overflow: hidden;
        `}
`

const Back = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  > ${Text}:hover {
    cursor: pointer;
    font-weight: 600;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  margin-top: 2em;
`;

const BackButton = styled(Button)`
  background-color: #008496;
  color: #FAF7F2;
  border: 1px solid #008496;
  height: 50px;
  font-size: 16px;
  font-family: GT America,sans-serif;
  line-height: 24px;
  padding: 0 3em;

  &:hover,
  &:focus {
    background-color: #9EB2B2;
    border-color: #9EB2B2;
    color: #FAF7F2;
  }
`;

class FormNavigator extends React.Component {
  state = {
    selectedFormId: null,
    formIdPath: null, // Defines the path of where data is being stored in formData state
    visible: false,
    formData: {},
    hasAllFormsCompleted: false,
    error: null,
  }

  componentDidMount() {
    // Due to some race conditions that come up by gatsby, we need to settle before render
    setTimeout(() => {
      this.setState({ visible: true })
    }, 100)
    if (this.props.retrieveUrl)
      this.requestDataFromUrl(this.props.retrieveUrl, this.props.queryString)
  }

  handleAllFormsCompleted = () => {
    if (!this.state.hasAllFormsCompleted) {
      this.setState({ hasAllFormsCompleted: true });
      const status = get(this.state.formData, 'status');
      if (this.props.updateUrl && status !== 'FULFILLED') {
        this.requestDataFromUrl(this.props.updateUrl, {
          isFinal: true,
          ...get(this.props, "queryString"),
        })
      }
    }
  }

  handleBackButtonClick = () => {
    window.location.assign('https://portal.hopkins.law');
  }

  requestDataFromUrl = (url, data) => {
    if (isUndefined(data.formId)) {
      this.setState({
        visible: false,
        error:
          "Ihr Link ist nicht mehr gültig. Bitte wenden Sie sich telefonisch oder per Email an uns für einen neuen Link.",
      })
    }
    if (!url) return
    return postData(`${url}`, data).then(result => {
      if (result) {
        if (result.error) {
          this.setState({
            visible: false,
            error:
              "Ihr Link ist nicht mehr gültig. Bitte wenden Sie sich telefonisch oder per Email an uns für einen neuen Link.",
          })
        } else {
          this.setState({ formData: { ...this.state.formData, ...result } })
        }
        if (data.data) {
          notification.success({
            message: "Angaben wurden gespeichert",
          })
        }
      }
    })
  }

  transformFormId = id => !isEmpty(id) && id.replace("drafts.", "drafts-")
  render() {
    const todoId = get(this.props, ['queryString', 'todoId'], null);
    return (
      <CustomLayout visible={this.state.visible}>
        {this.state.error && (
          <LoadingOverlay visible={true}>
            <Title>{this.state.error}</Title>
            <Title size="normal">kontakt@hopkins.law</Title>
            <Title size="normal">Tel: 0800 7 150 150</Title>
          </LoadingOverlay>
        )}
        <LoadingOverlay visible={!this.state.visible}></LoadingOverlay>
        <Layout show={this.state.selectedFormId == null} animate>
          <Title size="medium" margin={{ bottom: "medium" }}>
            {this.props.name}
          </Title>
          <FormGroups
            formGroups={this.props.formGroups}
            formData={this.state.formData}
            transformFormId={this.transformFormId}
            onAllFormsCompleted={this.handleAllFormsCompleted}
            selectForm={(formId, submissionIdentifier, dataId) =>
              this.setState({
                selectedFormId: formId,
                submissionIdentifier,
                formIdPath: dataId || formId,
              })
            }
          />
          {(this.state.hasAllFormsCompleted && todoId) && (
            <ButtonContainer>
              <BackButton onClick={this.handleBackButtonClick}>
                Zur Startseite
              </BackButton>
            </ButtonContainer>
          )}
        </Layout>
        <Layout show={this.state.selectedFormId != null}>
          {this.state.selectedFormId && (
            <Form
              formIdentifierOnly
              form={{
                _id: this.state.selectedFormId.replace("drafts-", "drafts."),
              }}
              state={get(this.state.formData, "data", {})}
              submissionIdentifier={this.state.submissionIdentifier}
              finishedForm={result => {
                let formIdPath = undefined
                let formId = this.state.selectedFormId
                if (
                  this.state.formIdPath
                    .replace("drafts.", "drafts-")
                    .indexOf(".") > -1
                ) {
                  formIdPath = first(this.state.formIdPath.split("."))
                }
                this.setState(
                  {
                    selectedFormId: null,
                    formIdPath: null,
                    formData: {
                      ...this.state.formData,
                      // Check if formId has a particular path that it should be saved to
                      // Relevant for multiplied forms. The result should be stored in an array
                      ...(formIdPath
                        ? set(
                            {
                              [formIdPath]: [
                                ...get(this.state.formData, formIdPath, []),
                              ],
                            },
                            this.state.formIdPath,
                            result
                          )
                        : { [this.state.formIdPath]: result }),
                    },
                  },
                  () => {
                    if (this.props.updateUrl) {
                      this.requestDataFromUrl(this.props.updateUrl, {
                        data: formIdPath
                          ? {
                              [formIdPath]: get(
                                this.state.formData,
                                formIdPath
                              ),
                            }
                          : {
                              [formId]: result,
                            },
                        ...get(this.props, "queryString"),
                      })
                    }
                  }
                )
              }}
            />
          )}
        </Layout>
      </CustomLayout>
    )
  }
}

export default withQueryString(FormNavigator)
